/**
 * Page Login publique — SmartLawyer
 * Firebase Auth : Google + Microsoft + email/password
 * Route : /login (en mode legalgraph)
 *
 * Coexiste avec le Login Chainlit (LegalChat)
 * Ne touche PAS à l'auth Chainlit/PostgreSQL
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { usePublicAuth } from '@/hooks/usePublicAuth';
import { PublicHeader, PublicFooter } from '@/components/jurisprudence';

export default function PublicLogin() {
  const { t } = useTranslation();
  const navigate      = useNavigate();
  const [params]      = useSearchParams();
  const redirectTo    = params.get('redirect') || '/';
  const intent        = params.get('intent');   // 'subscribe' etc.

  const {
    user, loading, error,
    loginWithGoogle, loginWithMicrosoft,
    loginWithEmail, registerWithEmail,
    clearError,
  } = usePublicAuth();

  const [mode,       setMode]       = useState<'login' | 'register'>('login');
  const [email,      setEmail]      = useState('');
  const [password,   setPassword]   = useState('');
  const [confirm,    setConfirm]    = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');

  // Redirect si déjà connecté
  useEffect(() => {
    if (!loading && user) {
      navigate(redirectTo, { replace: true });
    }
  }, [user, loading, navigate, redirectTo]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (mode === 'register') {
      if (password !== confirm) {
        setLocalError(t('auth.errors.password_mismatch'));
        return;
      }
      if (password.length < 8) {
        setLocalError(t('auth.errors.password_too_short'));
        return;
      }
      setSubmitting(true);
      await registerWithEmail(email, password);
    } else {
      setSubmitting(true);
      await loginWithEmail(email, password);
    }
    setSubmitting(false);
  };

  const displayError = localError || error;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PublicHeader />

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm">

          {/* Titre */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold tracking-tight mb-1">
              {mode === 'login' ? t('auth.login_title') : t('auth.register_title')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {mode === 'login'
                ? t('auth.login_subtitle')
                : t('auth.register_subtitle')}
            </p>
            {intent === 'subscribe' && (
              <div className="mt-3 inline-block bg-primary/10 text-primary text-xs font-medium px-3 py-1 rounded-full">
                {t('auth.subscribe_intent')}
              </div>
            )}
          </div>

          {/* Error */}
          {displayError && (
            <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive">
              {displayError}
            </div>
          )}

          {/* OAuth Buttons */}
          <div className="space-y-3 mb-6">
            <button
              onClick={loginWithGoogle}
              disabled={submitting}
              className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border bg-background hover:bg-accent transition-colors text-sm font-medium disabled:opacity-50"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth.continue_google')}
            </button>

            <button
              onClick={loginWithMicrosoft}
              disabled={submitting}
              className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border bg-background hover:bg-accent transition-colors text-sm font-medium disabled:opacity-50"
            >
              <svg className="w-4 h-4" viewBox="0 0 21 21">
                <rect x="1"  y="1"  width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1"  width="9" height="9" fill="#7FBA00"/>
                <rect x="1"  y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              {t('auth.continue_microsoft')}
            </button>
          </div>

          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-3 text-xs text-muted-foreground">{t('auth.or')}</span>
            </div>
          </div>

          {/* Email form */}
          <form onSubmit={handleEmailSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1.5">{t('auth.email')}</label>
              <input
                type="email" required
                value={email} onChange={e => setEmail(e.target.value)}
                placeholder={t('auth.email_placeholder')}
                className="w-full h-10 px-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium">{t('auth.password')}</label>
                {mode === 'login' && (
                  <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground">
                    {t('auth.forgot_password')}
                  </Link>
                )}
              </div>
              <input
                type="password" required minLength={8}
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-10 px-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium mb-1.5">{t('auth.confirm_password')}</label>
                <input
                  type="password" required minLength={8}
                  value={confirm} onChange={e => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-10 px-3 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || loading}
              className="w-full h-10 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {submitting
                ? t('auth.loading')
                : mode === 'login' ? t('auth.submit_login') : t('auth.submit_register')}
            </button>
          </form>

          {/* Toggle mode */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            {mode === 'login' ? (
              <>{t('auth.no_account')}{' '}
                <button
                  onClick={() => { setMode('register'); setLocalError(''); clearError(); }}
                  className="text-primary font-medium hover:underline"
                >
                  {t('auth.sign_up')}
                </button>
              </>
            ) : (
              <>{t('auth.have_account')}{' '}
                <button
                  onClick={() => { setMode('login'); setLocalError(''); clearError(); }}
                  className="text-primary font-medium hover:underline"
                >
                  {t('auth.sign_in')}
                </button>
              </>
            )}
          </p>

          {/* Mentions */}
          <p className="text-center text-xs text-muted-foreground mt-8">
            <Trans
              i18nKey="auth.terms_notice"
              components={{
                link: <Link to="/mentions-legales" className="underline hover:text-foreground" />,
              }}
            />
          </p>

        </div>
      </main>

      <PublicFooter />
    </div>
  );
}